import { useState, useCallback } from 'react'
import { api } from '../utils/apiComponent/apiClient'
import { endpoints } from '../utils/endpoints'

export function useCategoryTranslations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Créer une traduction pour une catégorie (admin)
  // L'API attend : { language, name, description }
  // L'API retourne : { id, categoryId, language, name, description, createdAt, updatedAt }
  const createTranslation = useCallback(async (categoryId, translationData) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.post(
        endpoints.createCategoryTranslation(categoryId),
        translationData
      )
      // L'API retourne : { success: true, message: [...], data: {...} }
      // ou directement l'objet traduction
      if (response?.data && response?.success) {
        return response.data
      }
      return response?.data || response
    } catch (err) {
      // Gérer les erreurs : message peut être un tableau ou une string
      const errorMessage = Array.isArray(err.response?.data?.message) 
        ? err.response.data.message[0] 
        : err.response?.data?.message || err?.message || "Erreur lors de la création de la traduction"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Récupérer toutes les traductions d'une catégorie
  // L'API retourne un tableau de traductions
  const getTranslations = useCallback(async (categoryId) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.get(endpoints.getCategoryTranslations(categoryId))
      // L'API retourne un tableau directement ou dans response.data
      return response?.data || response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la récupération des traductions"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Récupérer une traduction spécifique
  const getTranslation = useCallback(async (categoryId, language) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.get(endpoints.getCategoryTranslation(categoryId, language))
      return response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la récupération de la traduction"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Mettre à jour une traduction (admin)
  // L'API retourne : { id, categoryId, language, name, description, createdAt, updatedAt }
  const updateTranslation = useCallback(async (categoryId, language, updateData) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.patch(
        endpoints.updateCategoryTranslation(categoryId, language),
        updateData
      )
      // La réponse est directement l'objet traduction ou dans response.data
      return response?.data || response
    } catch (err) {
      // Gérer les erreurs : message peut être un tableau ou une string
      const errorMessage = Array.isArray(err.response?.data?.message) 
        ? err.response.data.message[0] 
        : err.response?.data?.message || err?.message || "Erreur lors de la mise à jour de la traduction"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Supprimer une traduction (admin)
  const deleteTranslation = useCallback(async (categoryId, language) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await api.delete(endpoints.deleteCategoryTranslation(categoryId, language))
      return response
    } catch (err) {
      const errorMessage = err?.message || "Erreur lors de la suppression de la traduction"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    createTranslation,
    getTranslations,
    getTranslation,
    updateTranslation,
    deleteTranslation
  }
}

